import { useMutation, useQuery } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { normalizeApiError } from '@/lib/apiEnvelope'
import { queryClient } from '@/lib/queryClient'
import { authStore } from '@/stores/authStore'

import { biometriaApi } from '../api/biometriaApi'
import { credencialLocal } from '../services/credencialLocal'
import { criarCredencial } from '../services/webauthn'

export const biometriaKeys = {
  all: ['biometria'] as const,
  list: () => [...biometriaKeys.all, 'aparelhos'] as const,
}

const invalidarAparelhos = () => queryClient.invalidateQueries({ queryKey: biometriaKeys.list() })

export function useAparelhosBiometria() {
  return useQuery({ queryKey: biometriaKeys.list(), queryFn: biometriaApi.listar })
}

/**
 * Opções → leitor biométrico → cadastro, numa tentativa só: o desafio é de uso único.
 * Sem toast de erro: o modal mostra a mensagem (cancelamento chega como `BiometriaCancelledError`).
 */
export function useCadastrarAparelho() {
  return useMutation({
    mutationFn: async (nomeDispositivo: string) => {
      const { desafioId, opcoes } = await biometriaApi.opcoesCadastro()
      const resposta = await criarCredencial(opcoes)
      return biometriaApi.cadastrar({ desafioId, nomeDispositivo, resposta })
    },
    onSuccess: (aparelho) => {
      void invalidarAparelhos()
      const { user, setOfertarBiometria } = authStore.getState()
      // Guarda o id para os próximos logins neste aparelho manterem o vínculo.
      if (aparelho.sessaoVinculada && user) credencialLocal.guardar(user.nomeUsuario, aparelho.id)
      setOfertarBiometria(false)
      toast.success('Aparelho cadastrado. Seu acesso agora é protegido pela biometria.')
    },
  })
}

export function useRemoverAparelho() {
  return useMutation({
    mutationFn: biometriaApi.remover,
    onSuccess: (_resultado, id) => {
      // Era o aparelho deste navegador: o id guardado não vale mais (permite cadastrar de novo).
      const user = authStore.getState().user
      if (user && credencialLocal.obter(user.nomeUsuario) === id) {
        credencialLocal.apagar(user.nomeUsuario)
      }
      void invalidarAparelhos()
      toast.success('Aparelho removido.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}
